import React from "react";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import { PurpleSwitch } from "../../../../helpers/helpers";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../../requests/shared/helpers/DrawerFooterContainerSmall";
import ButtonFilled from "../../requests/shared/helpers/ButtonFilled";
import ButtonGrayed from "../../requests/shared/helpers/ButtonGrayed";

// Add/edit one checklist item. Uses the app's shared drawer chrome
// (DrawerHeader / DrawerFooterContainerLarge / Button helpers); the type choice
// is presented as option cards (self-documenting) rather than a dropdown, and
// the two completion flags are toggle rows with their mutual exclusion
// preserved: a photo-required item cannot be optional, and vice versa.

const TYPE_OPTIONS = [
  {
    value: "checkbox",
    label: "Checkbox",
    hint: "Tap to check it off",
  },
  {
    value: "text",
    label: "Text answer",
    hint: "Requires a typed response, like a temperature",
  },
  {
    value: "pass_fail",
    label: "Pass / Fail",
    hint: "An inspection item judged against the standard in its description",
  },
  {
    value: "heading",
    label: "Section heading",
    hint: "A label that groups the items below it",
  },
];

const inputSx = {
  sx: {
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: "14px",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
    },
  },
};

function ToggleRow({ label, hint, checked, onChange }) {
  return (
    <div className="w-full flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
      <span>
        <span className="text-sm font-medium text-slate-800 block">{label}</span>
        <span className="text-xs text-slate-400">{hint}</span>
      </span>
      <PurpleSwitch
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </div>
  );
}

function SubtaskDrawer(props) {
  const data = props.subtaskData || {};

  const setField = (name, value) => {
    props.setSubtaskData((prevData) => {
      const next = { ...prevData, [name]: value };
      if (name === "type" && value === "heading") {
        // Headings are inert dividers: never optional-toggleable, never
        // require a photo. Force the flags so the UI and payload stay
        // consistent with the backend normalization.
        next.is_optional = true;
        next.is_image_required = false;
      }
      if (name === "type" && value === "pass_fail" && prevData?.type !== "pass_fail") {
        // An inspection item is answered by default: a scored audit needs
        // required items, and the new-item default is optional.
        next.is_optional = false;
      }

      if (name === "is_optional" && value) {
        next.is_image_required = false;
      }
      if (name === "is_image_required" && value) {
        next.is_optional = false;
      }
      return next;
    });
  };

  const isHeading = data.type === "heading";
  const isPassFail = data.type === "pass_fail";
  const canSave = (data.name || "").trim().length > 0;
  const saveLabel = props.subtaskEdit ? "Save Changes" : "Add Item";

  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose}>
      <div className="w-[450px] h-full bg-white relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader
            title={props.subtaskEdit ? "Edit Checklist Item" : "Add Checklist Item"}
            onClose={props.onClose}
          />
          <div className="w-full px-6">
            <div>
              <p className="text-sm text-slate-500">Name</p>
              <div className="mt-1">
                <TextField
                  placeholder={isHeading ? "Before opening" : "Wipe down prep stations"}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={data.name || ""}
                  onChange={(e) => setField("name", e.target.value)}
                  InputProps={inputSx}
                />
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm text-slate-500">
                {isPassFail ? "Standard (what passes)" : "Description (optional)"}
              </p>
              <div className="mt-1">
                <TextField
                  placeholder={
                    isPassFail
                      ? "PPM between 50 and 200, clean bucket, test strips available"
                      : "Any extra detail the person needs"
                  }

                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  size="small"
                  value={data.description || ""}
                  onChange={(e) => setField("description", e.target.value)}
                  InputProps={inputSx}
                />
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm text-slate-500">Type</p>
              <div className="mt-1 flex flex-col gap-2">
                {TYPE_OPTIONS.map((opt) => {
                  const active = (data.type || "checkbox") === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setField("type", opt.value)}
                      className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${
                        active
                          ? "border-violet-600 bg-violet-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <span className="flex items-center gap-1">
                        <Radio
                          checked={active}
                          size="small"
                          disableRipple
                          sx={{
                            padding: "2px",
                            color: "#cbd5e1",
                            "&.Mui-checked": { color: "#6d28d9" },
                          }}
                        />
                        <span
                          className={`text-sm font-semibold ${
                            active ? "text-violet-900" : "text-slate-800"
                          }`}
                        >
                          {opt.label}
                        </span>
                      </span>
                      <span className="text-xs text-slate-400 block mt-0.5 ml-[30px]">
                        {opt.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {!isHeading && (
              <div className="mt-6 border border-slate-200 rounded-xl px-4 bg-white">
                <ToggleRow
                  label="Optional"
                  hint="The task can be submitted without this item"
                  checked={!!data.is_optional}
                  onChange={(v) => setField("is_optional", v)}
                />
                <ToggleRow
                  label="Photo required"
                  hint="A photo must be attached to complete this item"
                  checked={!!data.is_image_required}
                  onChange={(v) => setField("is_image_required", v)}
                />
              </div>
            )}
          </div>

          <DrawerFooterContainerSmall>
            <div className="w-full">
              {canSave ? (
                <ButtonFilled
                  text={saveLabel}
                  onClick={() => props.onSave(props.subtaskData)}
                />
              ) : (
                <ButtonGrayed text={saveLabel} />
              )}
            </div>
          </DrawerFooterContainerSmall>
          <div className="h-[130px]"></div>
        </div>
      </div>
    </Drawer>
  );
}

export default SubtaskDrawer;
